import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from "prop-types";
import { Tabs } from 'antd';
import CoverLetterTab from "./CoverLetterTab";
import AvailabilityRateTab from "./AvailabilityRateTab";
import MatchScoreTab from "./MatchScoreTab";
import {useQueryClient} from '@tanstack/react-query';
import {useAuth} from "../../providers/AuthProvider";
import MessagesTab from "../proposals/MessagesTab";
import MeetingsTab from "./MeetingsTab";
import UserImageCircle from "../../components/UserImageCircle";
import {useProject} from "../../providers/ClientProjectProvider";
import CandidateProposalActions from "./components/CandidateProposalActions";
import AssignCandidateModal from "../project-bidding/components/AssignCandidateModal";
import {useMedia, usePrevious} from "react-use";
import TalentCandidate from "../../models/TalentCandidate";
import {Link, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {useAPI} from "../../config/API";
import PositionFixedFeeTab from "./PositionFixedFeeTab";
import PositionFixedRetainerTab from "./PositionFixedRetainerTab";

const CandidateProposal = (props) => {
    const {project} = useProject();
    const [position] = useState(props.position);
    const {auth} = useAuth();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const isLargeScreenWidth = useMedia('(max-width: 1024px)'); // 1024 matches tailwind lg size

    useEffect(() => {
        if (!isLargeScreenWidth && selectedTab === 'messages') {
            setSelectedTab(tabs[0]);
        }
    }, [isLargeScreenWidth])

    const updateCandidate = async(candProps = {}) => {
        let update = TalentCandidate.make({
            ...props.candidate,
            ...candProps
        })
        await props.position.resetCandidateQueryDataForTalent(update);

        if(props.onUpdated) props.onUpdated(update);
    }

    const {
        data,
        isLoading,
        error
    } = useAPI(['markProposalAsViewed', {
        client_id: auth.clientOrg?.()?.id,
        project_id: project?.id,
        position_id: position?.id,
        candidate_id: props.candidate?.id
    }], {
        enabled: !!position?.id && !project.isActive(),
        onSuccess: async(data) => {
            let viewedAt = props.candidate.job_candidate.proposal_updated_at;
            let marked = TB.moment()
            // if(viewedAt && TB.moment(viewedAt).isAfter(marked)) {
            //     marked = TB.moment(viewedAt);
            // }
            let candidateJobData = TB.set(
                {...props.candidate},
                'job_candidate.last_viewed_at',
                marked.toDate()
            );

            await updateCandidate(candidateJobData);
        }
    })

    const canRequestTalentInterviewForProject = auth.canRequestTalentInterviewForProject(props.project);

    const interviewBadge = () => {
        if (!canRequestTalentInterviewForProject) {
            return <i className="fa fa-lock" style={{fontSize: 12}}/>
        }

        if (project.is_on_hold || (!props.candidate.is_assigned && position.is_filled)) {
            return null;
        }

        if(!props.candidate.has_proposed_interview &&
            !props.candidate.upcoming_meetings_count &&
            !props.candidate.proposed_meetings_count && !props.candidate.latest_meeting_update_at
        ) {
            return (
                <span className="pulse-indicator pulse-relative">
                    <span className="blob info"></span>
                </span>
            )
        }

        // Show badge if interview was updated within the past hour
        if(props.candidate.hasInterviewUpdateSinceLastViewed()) {
            return <i className="fa fa-exclamation-circle text-danger" style={{fontSize: 12}}/>
        }

        return null;
    }

    const messageBadge = () => {
        if (!!project.unread_messages_count) {
            return (
                <span className="pulse-indicator pulse-relative">
                    <span className="blob info"></span>
                </span>
            )
        }
        return null;
    }

    const proposalTab = props.candidate.isFixedFee() ? "fixed-fee-proposal" : (
        props.candidate.isFixedRetainer() ? 'fixed-retainer-proposal' : 'time-materials-proposal'
    )

    const tabs = [
        "cover-letter",
        "match-summary",
        proposalTab,
        "interviews",
    ]


    const previousLocationSearch = usePrevious(searchParams.get('tabView'));

    const selectedTab = useMemo(() => {
        if (props.selectedTab) return props.selectedTab;
        let tab;
        if(searchParams.has('tabView')) {
            tab =  TB.find(tabs, tab => TB.slugify(tab) === searchParams.get('tabView'));
        }
        if(!tab) {
            tab = previousLocationSearch || TB.first(tabs)
        }
        return tab;
    }, [location.search, props.selectedTab]);


    const setSelectedTab = (tab) => {
        if(props.onTabChange) {
            return props.onTabChange(tab)
        }
        let params = new URLSearchParams(searchParams);
        params.set('tabView', tab);
        return setSearchParams(params, {replace:true})
    };


    const detailsLink = useMemo(() => {
        const user = props.candidate;

        let search = new URLSearchParams(searchParams);
        if (search.has('tId')) {
            search.delete('tId')
        }

        return {
            ...location,
            search: `?${search.toString()}`,
            pathname: location.pathname + `/talent-profiles/${user.id}`,
        }
    }, [location.pathname, props.backBtnTitle])


    const canInviteTalentForProject = project && auth?.canInviteTalentForProject(project)
    const canAssignTalentForProject = project && auth?.canAssignTalentForProject(project)

    const runProjectTalentRequest = async(method, successKey) => {
        let candidate = TalentCandidate.make({...props.candidate});

        let wasSuccess = await props.onRequest(async () => {
            let updatedCandidate = await candidate[method](
                project, position
            )

            if (!updatedCandidate) {
                return false;
            }

            let callback = 'on' + TB.upperFirst(method);
            if (props[callback]) {
                await props[callback](updatedCandidate);
            }

            return true;
        });

        if (wasSuccess) {
            await TB.sysAlert(successKey);
        }
    }

    const removeFromCandidates = async() => {
        if (!canInviteTalentForProject) {
            return TB.showPermissionError();
        }
        return runProjectTalentRequest(
            'removeFromCandidates', 'byo.remove_from_candidate_button_clicked'
        );
    }

    const addToCandidates = async() => {
        let max_candidates = parseInt(laravel.platform_settings.Max_Candidates_Per_BYO_Project_Role);

        if (TB.isNaN(max_candidates)) {
            max_candidates = 10;
        }
        let current_candidates = position?.total_selected_candidates;

        if (current_candidates.length >= max_candidates) {
            return TB.sysAlert('byo.add_as_candidate_button_clicked_tip_max_candidates')
        }
        let hasSeenTip = await TB.sysAlert('byo.add_as_candidate_button_clicked_tip', {max_candidates})

        if (hasSeenTip) {
            return runProjectTalentRequest(
                'addToCandidates', 'byo.add_as_candidate_button_clicked'
            );
        }
    }

    const assignCandidate = async() => {

        if (!props.candidate.isShortlisted()) {
            return TB.sysAlert('byo.cant_select_uninterested_talent_for_role');
        }

        return TB.event("Modal@show", {
            modalClass: "assign-candidate-modal",
            content: (
                <AssignCandidateModal
                    position={position}
                    candidate={props.candidate}
                    onConfirm={async() => {
                        await runProjectTalentRequest('assignToPosition',
                            !TB.has(TB.storedPreferences(), `hide_tips.byo.select_for_role_button_clicked_tip`)
                                ? 'byo.select_for_role_button_clicked_tip'
                                : 'byo.select_for_role_button_clicked'
                        )
                    }}
                />
            )
        })


    }

    const unassignCandidate = () => {
        if (project.hasRequestedSOW()) {
            return TB.toast("Can't reopen after SOW has already been requested!", {theme: 'danger'})
        }
        if (position.assigned_candidate?.id !== props.candidate?.id) {
            return;
        }

        return runProjectTalentRequest(
            'unassignFromPosition', 'byo.removed_for_role_button_clicked'
        );
    }


    return (
        <div className="proposal-container candidate-proposal">

            {!props.minimal && (
                <div className="proposal-header">
                    {props.onClose && (
                        <button className="btn btn-link-primary proposal-back-btn" onClick={props.onClose}>
                            <i className="fa fa-angle-left mr-2"/>
                            {props.backBtnTitle}
                        </button>
                    )}
                    <h2 className="mb-0">
                        Candidate Proposal

                        {props.candidate?.talent_proposal_updated_at && (
                            <small className="ml-3">
                                Last updated: {TB.moment(props.candidate?.talent_proposal_updated_at).fromNow()}
                            </small>
                        )}
                    </h2>
                </div>
            )}

            <div className="proposal-info">
                {!props.minimal && (
                    <div className="candidate-container">

                        <div className="candidate-info flex-col md:flex-row items-center">
                            <UserImageCircle
                                user={props.candidate} size={100}
                                hideTooltip={true}
                                className="mr-1 img-grayscale"/>
                            <div className="flex flex-col items-center md:items-start gap-12">
                                <div className="flex flex-col items-center md:items-start gap-6 md:gap-0">
                                    <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
                                   
                                        <h5>{props.candidate?.firstNamePlusInitial()}</h5>
                                        <p className="candidate-background">
                                            <i className="fa fa-info-circle"/>
                                            {props.candidate?.job_title}
                                        </p>


                                        {props.candidate?.location && (
                                            <p className="candidate-background">
                                                <i className="fa fa-map-marker-alt"/>
                                                {props.candidate?.location.city}, {props.candidate?.location.state}
                                            </p>
                                        )}

                                    </div>
                                    {props.candidate?.bio && (
                                        <p className="truncating-text team-bio text-center md:text-left">
                                            {props.candidate?.fullBio()}
                                        </p>
                                    )}
                                </div>

                                {!location?.pathname?.includes('talent-profiles') && (
                                    <Link className="details-link"
                                          to={detailsLink} state={{backBtnTitle: props.backBtnTitle}}>
                                        See Details
                                    </Link>
                                )}
                            </div>
                        </div>

                        <CandidateProposalActions
                            positionAssignable={props.positionAssignable}
                            candidate={props.candidate}
                            assigned={position?.assigned_candidate}
                            onUnassignCandidate={unassignCandidate}
                            onAssignCandidate={assignCandidate}
                            onAddCandidate={addToCandidates}
                            onRemoveCandidate={removeFromCandidates}
                            isDisabled={props.isDisabled || project.isActive()}
                            canAssignTalentForProject={canAssignTalentForProject}
                            canInvite={canInviteTalentForProject}/>
                    </div>
                )}
          
                <div className="proposal-overview block lg:flex">
                    <div className="proposal-tabs-section">

                        <Tabs defaultActiveKey="cover-letter">
                            <Tabs.TabPane tab="Cover Letter" key="cover-letter">
                                <div className="proposal-tab-content">
                                    <CoverLetterTab candidate={props.candidate}/>
                                </div>
                            </Tabs.TabPane>

                            {auth.canViewTalentPricing() && (
                                <>
                                    {props.candidate.isTimeMaterials() && (
                                        <Tabs.TabPane tab="Availability & Rate" key="time-materials-proposal">
                                            <div className="proposal-tab-content">
                                                <AvailabilityRateTab candidate={props.candidate} position={position} />
                                            </div>
                                        </Tabs.TabPane>
                                    )}
                                    {props.candidate.isFixedFee() && (
                                        <Tabs.TabPane tab="Proposed Scope" key="fixed-fee-proposal">
                                            <div className="proposal-tab-content">
                                                <PositionFixedFeeTab candidate={props.candidate} position={position} />
                                            </div>
                                        </Tabs.TabPane>
                                    )}
                                    {props.candidate.isFixedRetainer() && (
                                        <Tabs.TabPane tab="Proposal" key="fixed-retainer-proposal">
                                            <div className="proposal-tab-content">
                                                <PositionFixedRetainerTab candidate={props.candidate} position={position} />
                                            </div>
                                        </Tabs.TabPane>
                                    )}
                                </>
                            )}

                            <Tabs.TabPane tab="Match Summary" key="match-summary">
                                <div className="proposal-tab-content">
                                    <MatchScoreTab candidate={props.candidate} position={position} projectId={project?.id} />
                                </div>
                            </Tabs.TabPane>

                            {props.candidate && (
                                <>
                                    <Tabs.TabPane tab={(<span className="flex gap-1.5">Interviews {interviewBadge()}</span>)} key="interviews">
                                        <div className="proposal-tab-content">
                                            <MeetingsTab candidate={props.candidate} position={position} />
                                        </div>
                                    </Tabs.TabPane>

                                    {!!isLargeScreenWidth && (
                                        <Tabs.TabPane tab="Messages" key="messages">
                                            <div className="proposal-messages candidate-messages m-0 min-h-[500px]">
                                                <MessagesTab
                                                    hasPermission={auth.canRequestTalentInterviewForProject(project)}
                                                    candidate={props.candidate}
                                                    position={props.position}
                                                    onFetchSuccess={() => {
                                                        let viewedAt = props.candidate.job_candidate.proposal_updated_at;
                                                        let marked = TB.moment()
                                                        let candidateJobData = TB.set({ ...props.candidate }, 'job_candidate.last_viewed_at', marked.toDate());
                                                        candidateJobData = TB.set(candidateJobData,'job_candidate.unread_messages_count', 0)
                                                        return updateCandidate(candidateJobData)
                                                    }}
                                                    hasRequestedSOW={props.project?.hasRequestedSOW()}
                                                />
                                            </div>
                                        </Tabs.TabPane>
                                    )}
                                </>
                            )}
                        </Tabs>
                    </div>
                    {props.candidate && (
                        <div className="proposal-messages candidate-messages hidden lg:flex lg:mt-[62px]">
                            <MessagesTab
                                hasPermission={auth.canRequestTalentInterviewForProject(project)}
                                candidate={props.candidate}
                                position={props.position}
                                onFetchSuccess={() => {
                                    let viewedAt = props.candidate.job_candidate.proposal_updated_at;
                                    let marked = TB.moment()
                                    let candidateJobData = TB.set({ ...props.candidate }, 'job_candidate.last_viewed_at', marked.toDate());
                                    candidateJobData = TB.set(candidateJobData,'job_candidate.unread_messages_count', 0)
                                    return updateCandidate(candidateJobData)
                                }}
                                hasRequestedSOW={props.project?.hasRequestedSOW()}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
};

CandidateProposal.propTypes = {
    isDisabled: PropTypes.bool,
    canRemove: PropTypes.any,
    onRemove: PropTypes.any,
    onExit: PropTypes.any,
    candidate: PropTypes.any,
    canRequestInterview: PropTypes.any,
    onRequestInterview: PropTypes.any,
    isSelected: PropTypes.any,
    canBeSelected: PropTypes.any,
    onSelected: PropTypes.any,
    backBtnTitle: PropTypes.any
};
const styles = {
    favoriteBtn: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'flex-end'
    },

    archiveBtn: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'flex-start'
    },
    inviteLockIcon: {
        position: 'absolute',
        fontSize: 13,
        right: -18,
    }
};

export default CandidateProposal;